import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { usePromoRotation, logPromoHeroEvent } from '../hooks/usePromoPopup';

const INTERVAL_MS = 7000;

/**
 * PromoHeroCard — a hero üres részére kerülő ajánlatkártya. Ugyanazokat a
 * promo típusokat mutatja, amik az adminban "Fut"-ra vannak állítva: 7
 * másodpercenként váltanak, nyilakkal/pöttyökkel lapozhatók, egérrel
 * ráállva megállnak.
 */
const PromoHeroCard = ({ onBooking, testId = 'promo-hero-card' }) => {
  const items = usePromoRotation();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const seen = useRef(new Set());

  const count = items?.length || 0;
  const promo = count > 0 ? items[index % count] : null;

  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), INTERVAL_MS);
    return () => clearInterval(t);
  }, [count, paused]);

  // Típusonként egyszer naplózunk megjelenítést.
  useEffect(() => {
    if (!promo) return;
    const key = promo.__variant || 'base';
    if (seen.current.has(key)) return;
    seen.current.add(key);
    logPromoHeroEvent('impression', promo);
  }, [promo]);

  const bullets = useMemo(() => {
    if (!promo) return [];
    return [
      [promo.bullet_lead, promo.bullet_accent].filter(Boolean).join(' '),
      promo.discount_amount ? `${promo.discount_amount} — ${promo.discount_sub || ''}`.trim() : '',
      promo.bullet_sub || promo.discount_fine || '',
    ].filter(Boolean);
  }, [promo]);

  if (!promo) return null;

  const onCta = () => {
    logPromoHeroEvent('click', promo);
    if (promo.cta_action === 'link' && promo.cta_href) {
      window.location.assign(promo.cta_href);
      return;
    }
    if (onBooking) onBooking();
  };

  return (
    <div
      data-testid={testId}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative w-full max-w-[400px] rounded-2xl border border-yellow-600/35 bg-[#0B1B33]/85 backdrop-blur-md p-6 shadow-2xl shadow-black/40"
      style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[10px] font-bold tracking-[0.26em] text-yellow-400">
          {promo.eyebrow || 'AKTUÁLIS AJÁNLAT'}
        </div>
        {count > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Előző ajánlat"
              data-testid={`${testId}-prev`}
              onClick={() => setIndex((i) => (i - 1 + count) % count)}
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              aria-label="Következő ajánlat"
              data-testid={`${testId}-next`}
              onClick={() => setIndex((i) => (i + 1) % count)}
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>

      <div key={promo.__variant || 'base'} className="animate-in fade-in duration-500">
        <div
          data-testid={`${testId}-headline`}
          className="text-[26px] sm:text-[30px] font-black leading-[1.02] text-white"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          {promo.headline_line1}
          <br />
          <span className="text-yellow-500">{promo.headline_line2}</span>
        </div>

        {promo.headline_badge && (
          <div className="inline-block mt-3 bg-yellow-500 text-[#0B1B33] font-black text-[11px] tracking-[0.16em] px-3 py-1.5 rounded">
            {promo.headline_badge}
          </div>
        )}

        <ul className="mt-4 space-y-2">
          {bullets.slice(0, 3).map((b, i) => (
            <li key={i} className="flex gap-2 text-[13px] text-white/80 leading-snug">
              <CheckCircle2 size={15} className="text-yellow-500 shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        data-testid={`${testId}-cta`}
        onClick={onCta}
        className="group mt-5 w-full bg-yellow-500 hover:bg-yellow-400 text-[#0B1B33] font-black text-[12px] tracking-[0.12em] rounded-lg px-4 py-3.5 flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-xl hover:shadow-yellow-500/25"
      >
        <span>{promo.cta_label}</span>
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" strokeWidth={3} />
      </button>

      {count > 1 && (
        <div className="mt-4 flex items-center justify-center gap-1.5">
          {items.map((it, i) => (
            <button
              key={it.__variant || i}
              type="button"
              aria-label={`${it.__label} ajánlat`}
              title={it.__label}
              data-testid={`${testId}-dot-${i}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index % count ? 'w-6 bg-yellow-500' : 'w-1.5 bg-white/30 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PromoHeroCard;
